// A17 — Infratuzilma va monitoring. REAL, deployed backend (live-read):
//   GET /admin/infra/services  — postgres/redis/minio ping+latency (A17.01)
//   GET /admin/infra/redis     — Redis INFO stats (A17.15)
//   GET /admin/infra/storage   — MinIO buckets: objects+size (A17.16)
//   GET /admin/infra/queue     — media job queue depth (A17.18)
// Boshqa A17 ekranlar (DC/incident/on-call/Horizon/deploy-history) = REAL infra
// kutadi → halol «planned» qoldirildi (soxta UI yo'q). HOT fayllarga tegmaydi.
import { api } from '@/lib/api'

export interface ServiceHealth {
  name: string
  up: boolean
  latency_ms: number
  detail: string
}
export interface RedisStats {
  version: string
  uptime_seconds: number
  connected_clients: number
  used_memory: number
  maxmemory: number
  used_memory_pct: number
  maxmemory_policy: string
  frag_ratio: number
  db_keys: number
  keyspace_hits: number
  keyspace_misses: number
  hit_rate: number
  evicted_keys: number
  expired_keys: number
  ops_per_sec: number
  aof_enabled: boolean
}
export interface QueueStats {
  media_jobs: number
}
export interface BucketStat {
  name: string
  reachable: boolean
  objects: number
  size_bytes: number
  truncated: boolean
}
export interface StorageStats {
  buckets: BucketStat[]
  total_objects: number
  total_size_bytes: number
}

/** A17.10 — one background-job run (the real MyGram error log lives in job_runs). */
export interface JobRunRow {
  id: number
  job_name: string
  status: string // ok|error
  detail: string
  items_affected: number
  duration_ms: number
  ran_at: string
}

/** A17.23 — one DB migration (embedded file vs schema_migrations version). */
export interface MigrationRow {
  version: string
  name: string
  applied: boolean
  applied_at: string | null
}

const opt = (signal?: AbortSignal) => (signal ? { signal } : undefined)

export const fetchServices = (s?: AbortSignal) =>
  api.get<{ services: ServiceHealth[] }>('/admin/infra/services', opt(s)).then((d) => d.services ?? [])
export const fetchRedis = (s?: AbortSignal) =>
  api.get<{ redis: RedisStats }>('/admin/infra/redis', opt(s)).then((d) => d.redis)
export const fetchQueue = (s?: AbortSignal) =>
  api.get<{ queue: QueueStats }>('/admin/infra/queue', opt(s)).then((d) => d.queue)
export const fetchStorage = (s?: AbortSignal) =>
  api.get<{ storage: StorageStats }>('/admin/infra/storage', opt(s)).then((d) => d.storage)

// A17.10 — errored background-job runs = MyGram's real application error log
// (there is no separate error-log store; failed detectors/housekeeping land here).
export const fetchJobErrors = (s?: AbortSignal) =>
  api
    .get<{ runs: JobRunRow[] }>('/admin/job-runs?status=error&limit=100', opt(s))
    .then((d) => d.runs ?? [])

// A17.23 — DB migration inventory (embedded files ↔ schema_migrations). Real,
// read-only (GET /admin/devtools/migrations, A27.10).
export const fetchMigrations = (s?: AbortSignal) =>
  api.get<{ migrations: MigrationRow[] }>('/admin/devtools/migrations', opt(s)).then((d) => d.migrations ?? [])

/** Baytlarni odam-o'qiydigan ko'rinishga (B/KB/MB/GB). */
export function fmtBytes(n: number): string {
  if (!n) return '0 B'
  const u = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1)
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`
}

/** Soniyalarni uptime ko'rinishiga (Nd Nh Nm). */
export function fmtUptime(sec: number): string {
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  return [d && `${d}d`, h && `${h}h`, `${m}m`].filter(Boolean).join(' ')
}

export const INFRA_STR = {
  services: 'Состояние сервисов',
  servicesDesc: 'PostgreSQL · Redis · MinIO — ping и задержка (реальный live-read)',
  redis: 'Состояние кэша (Redis)',
  redisDesc: 'Redis INFO — память, попадания, операции',
  storage: 'Файловое хранилище (MinIO)',
  storageDesc: 'Бакеты: объекты и размер',
  queue: 'Очередь обработки медиа',
  queueDesc: 'Глубина очереди медиа-задач',
  up: 'Работает',
  down: 'Недоступен',
  latency: 'Задержка',
  refresh: 'Обновить',
  loadFailed: 'Ошибка загрузки',
  // redis fields
  version: 'Версия',
  uptime: 'Аптайм',
  clients: 'Клиенты',
  memory: 'Память',
  policy: 'Политика вытеснения',
  hitRate: 'Hit-rate',
  ops: 'Опер/сек',
  keys: 'Ключи',
  evicted: 'Вытеснено',
  expired: 'Истекло',
  frag: 'Фрагментация',
  aof: 'AOF',
  // storage / queue
  bucket: 'Бакет',
  objects: 'Объекты',
  size: 'Размер',
  reachable: 'Доступен',
  total: 'Всего',
  pending: 'В очереди',
  mediaJobs: 'Медиа-задачи',
  empty: 'Очередь пуста',
  // A17.10 — xato loglari (errored job runs)
  errorLogs: 'Логи ошибок',
  errorLogsDesc: 'Упавшие фоновые задания (job_runs.status=error) — реальный журнал ошибок MyGram',
  noErrors: 'Ошибок нет — все фоновые задания успешны',
  job: 'Задание',
  detail: 'Детали',
  duration: 'Длительность',
  when: 'Когда',
  // A17.14 — DB holati va replikatsiya
  db: 'Состояние БД и репликация',
  dbDesc: 'PostgreSQL — ping и задержка (live-read). MyGram — одноузловой деплой',
  dbHealth: 'Здоровье PostgreSQL',
  replication: 'Репликация',
  singleNode: 'Одноузловой деплой — реплика не настроена',
  primary: 'Primary',
  replica: 'Реплика',
  none: 'Нет',
  // A17.23 — migratsiya holati (DB)
  migrations: 'Состояние миграций (БД)',
  migrationsDesc: 'Инвентарь миграций схемы: применённые vs ожидающие (schema_migrations)',
  migration: 'Миграция',
  applied: 'Применена',
  pendingMig: 'Ожидает',
  appliedCount: 'Применено',
  pendingCount: 'Ожидает',
  noMigrations: 'Миграции не найдены',
} as const
